import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { colors, radii } from '../../theme/tokens';
import { Icon } from '../ui';
import { subNavPill } from '../ui/SubNav';
import { useToast } from '../Toast';
import { tasksApi } from '../../lib/tasksApi';
import { Avatar, statusMeta } from './taskHelpers';
import useIsDesktop from '../../hooks/useIsDesktop';

const PERIODS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' }
];

// Fixed (non-configurable) activity types. Lead "quick actions" are added
// dynamically from the admin-configurable list (see useLeadActionMeta) so new
// actions get a card + label with no code change.
const FIXED_TYPE_META = [
  { key: 'todo_done', label: 'Todos done', icon: 'check_circle' },
  { key: 'task_create', label: 'Tasks created', icon: 'add_task' },
  { key: 'task_done', label: 'Tasks completed', icon: 'task_alt' },
  { key: 'lead_convert_stable', label: 'Leads → Stable', icon: 'trending_up' }
];

// Singular labels for the per-person report log (one row = one action).
const FIXED_ACTION_LABEL = {
  todo_done: 'Checklist item done',
  task_create: 'Task created',
  task_done: 'Task completed',
  message: 'Message sent',
  lead_convert_stable: 'Lead → Stable',
  client_convert_unstable: 'Client → Unstable',
  client_convert_technical: 'Client → Technical'
};

// Fetches the admin-configurable lead quick actions once and derives the
// label/card maps used across the analytics views. Falls back gracefully to
// the raw type key if the list can't be loaded.
function useLeadActionMeta() {
  const [leadActions, setLeadActions] = useState([]);
  useEffect(() => {
    let alive = true;
    tasksApi.leadActions().then((list) => { if (alive) setLeadActions(list || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return useMemo(() => {
    const quickMeta = leadActions.map((a) => ({ key: a.type, label: a.label, icon: 'ads_click' }));
    const typeMeta = [...FIXED_TYPE_META, ...quickMeta];
    const typeLabel = typeMeta.reduce((m, t) => { m[t.key] = t.label; return m; }, {});
    const actionLabelMap = { ...FIXED_ACTION_LABEL };
    leadActions.forEach((a) => { actionLabelMap[a.type] = a.label; });
    const actionLabel = (t) => actionLabelMap[t] || typeLabel[t] || t;
    return { typeMeta, typeLabel, actionLabel };
  }, [leadActions]);
}

const pad2n = (n) => String(n).padStart(2, '0');
const localDay = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad2n(x.getMonth() + 1)}-${pad2n(x.getDate())}`;
};
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};
const fmtDateTime = (d) => (d
  ? new Date(d).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—');
const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

// Intensity ramp for a cell, GitHub-contribution style (green theme). Scales
// on weighted points (a 10-point conversion lands near the top of the ramp).
function cellColor(count, max) {
  if (!count) return 'transparent';
  const t = max > 0 ? count / max : 1;
  const alpha = 0.25 + 0.6 * t;
  return `rgba(47,182,92,${alpha.toFixed(3)})`;
}

// Action-engagement analytics. Heatmap of consolidated actions per person,
// bucketed hourly (Today) or daily (Week/Month). Hovering a block reveals the
// tasks behind those actions.
const TaskAnalytics = ({ departments = [] }) => {
  const showToast = useToast();
  const isDesktop = useIsDesktop();
  const [view, setView] = useState('overview'); // 'overview' | 'department'
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null); // { x, y, bucket, cell, userName }
  // Clicking a person's name opens their date-ranged performance report.
  const [reportUser, setReportUser] = useState(null); // { userId, name, picture }

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await tasksApi.analytics(p);
      setData(res);
    } catch (_) {
      showToast('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { if (view === 'overview') load(period); }, [period, load, view]);

  const buckets = data?.buckets || [];
  const users = data?.users || [];
  const maxCell = data?.maxCell || 0;
  const cellW = data?.granularity === 'hour' ? 28 : (period === 'month' ? 26 : 46);

  const peopleActive = users.length;
  const totalPoints = data?.totalPoints || 0;

  const enterCell = (e, bucket, cell, userName) => {
    setHover({ x: e.clientX, y: e.clientY, bucket, cell, userName });
  };
  const moveCell = (e) => setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h));
  const leaveCell = () => setHover(null);

  const { typeMeta, typeLabel } = useLeadActionMeta();
  const typeTotals = useMemo(() => data?.typeTotals || {}, [data]);
  const activeTypes = useMemo(() => typeMeta.filter((t) => (typeTotals[t.key] || 0) > 0), [typeMeta, typeTotals]);

  const renderCell = (u, b) => {
    const cell = u.cells[b.key];
    const points = cell?.points ?? cell?.count ?? 0;
    return (
      <div
        key={b.key}
        style={{
          ...styles.cell,
          width: cellW,
          background: cellColor(points, maxCell),
          borderColor: points ? 'transparent' : colors.borderSoft,
          color: points ? colors.white : 'transparent',
          cursor: points ? 'pointer' : 'default'
        }}
        onMouseEnter={points ? (e) => enterCell(e, b, cell, u.name) : undefined}
        onMouseMove={points ? moveCell : undefined}
        onMouseLeave={points ? leaveCell : undefined}
      >
        {points || ''}
      </div>
    );
  };

  if (reportUser) {
    return <UserReport user={reportUser} onBack={() => setReportUser(null)} />;
  }

  return (
    <div>
      <div style={styles.viewTabs}>
        <button style={subNavPill(view === 'overview')} onClick={() => setView('overview')}>
          <Icon name="insights" size={15} color={view === 'overview' ? colors.parGreenBright : colors.text} /> Activity
        </button>
        <button style={subNavPill(view === 'department')} onClick={() => setView('department')}>
          <Icon name="account_tree" size={15} color={view === 'department' ? colors.parGreenBright : colors.text} /> By Department
        </button>
      </div>

      {view === 'department' ? (
        <DepartmentBreakdown departments={departments} onPickUser={setReportUser} />
      ) : (
      <>
      <div style={styles.head}>
        <h2 style={styles.title}><Icon name="insights" size={20} color={colors.parGreenBright} /> Activity</h2>
        <div style={styles.periods}>
          {PERIODS.map((p) => (
            <button key={p.value} style={subNavPill(period === p.value)} onClick={() => setPeriod(p.value)}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading analytics…</div>
      ) : (
        <>
          <div style={styles.summaryCards}>
            <div style={styles.card}>
              <div style={styles.cardLabel}>Total points</div>
              <div style={styles.cardValue}>{totalPoints}</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>People active</div>
              <div style={styles.cardValue}>{peopleActive}</div>
            </div>
            {activeTypes.map((t) => (
              <div key={t.key} style={styles.card}>
                <div style={styles.cardLabel}><Icon name={t.icon} size={13} color={colors.textMuted} /> {t.label}</div>
                <div style={styles.cardValue}>{typeTotals[t.key] || 0}</div>
              </div>
            ))}
          </div>

          {users.length === 0 ? (
            <div style={styles.empty}>
              <Icon name="grid_view" size={36} color={colors.textFaint} />
              <p style={{ marginTop: 10 }}>No activity in this period yet. Points are earned by completing todos and tasks, and by lead conversions and quick actions in Client Tracking.</p>
            </div>
          ) : (
            <>
              {isDesktop ? (
                <div style={styles.scroll}>
                  <div>
                    {/* Header row of bucket labels */}
                    <div style={styles.gridRow}>
                      <div style={styles.nameCell} />
                      {buckets.map((b) => (
                        <div key={b.key} style={{ ...styles.colLabel, width: cellW }}>{b.label}</div>
                      ))}
                    </div>

                    {users.map((u) => (
                      <div key={u.userId} style={styles.gridRow}>
                        <div style={styles.nameCell}>
                          <Avatar user={{ name: u.name, picture: u.picture }} size={24} />
                          <button
                            type="button"
                            style={styles.nameBtn}
                            onClick={() => setReportUser({ userId: u.userId, name: u.name, picture: u.picture })}
                            title={`View ${u.name}'s report`}
                          >
                            {u.name}
                          </button>
                          <span style={styles.nameTotal}>{u.total}</span>
                        </div>
                        {buckets.map((b) => renderCell(u, b))}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Mobile: stack the block strip under each person's name for width.
                <div style={styles.mobileList}>
                  {users.map((u) => (
                    <div key={u.userId} style={styles.mUser}>
                      <div style={styles.mNameLine}>
                        <Avatar user={{ name: u.name, picture: u.picture }} size={24} />
                        <button
                          type="button"
                          style={styles.nameBtn}
                          onClick={() => setReportUser({ userId: u.userId, name: u.name, picture: u.picture })}
                          title={`View ${u.name}'s report`}
                        >
                          {u.name}
                        </button>
                        <span style={styles.nameTotal}>{u.total}</span>
                      </div>
                      <div style={styles.scroll}>
                        <div style={styles.mBlocksRow}>
                          {buckets.map((b) => renderCell(u, b))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={styles.legend}>
                <span style={styles.legendText}>Less</span>
                {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
                  <span key={i} style={{ ...styles.legendBox, background: t === 0 ? 'transparent' : cellColor(Math.max(1, Math.round(t * maxCell)), maxCell), borderColor: t === 0 ? colors.borderSoft : 'transparent' }} />
                ))}
                <span style={styles.legendText}>More</span>
                <span style={styles.legendNote}>Blocks show weighted points — a lead→stable conversion is worth 5.</span>
              </div>
            </>
          )}
        </>
      )}

      {hover && ReactDOM.createPortal((
        <div style={{ ...styles.tooltip, top: hover.y + 16, left: Math.min(hover.x + 16, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 260) }}>
          <div style={styles.tipHead}>{hover.userName} · {hover.bucket.full}</div>
          <div style={styles.tipCount}>
            {(hover.cell.points ?? hover.cell.count)} point{(hover.cell.points ?? hover.cell.count) === 1 ? '' : 's'}
            <span style={styles.tipActions}> · {hover.cell.count} action{hover.cell.count === 1 ? '' : 's'}</span>
          </div>
          <div style={styles.tipTypes}>
            {Object.keys(hover.cell.byType || {}).map((k) => (
              <span key={k} style={styles.tipType}>{typeLabel[k] || k}: {hover.cell.byType[k]}</span>
            ))}
          </div>
          {(hover.cell.tasks || []).length > 0 && (
            <div style={styles.tipTasks}>
              <div style={styles.tipTasksHead}>Tasks &amp; clients</div>
              {hover.cell.tasks.map((t, i) => (
                <div key={i} style={styles.tipTask}>
                  <span style={styles.tipTaskTitle}>{t.title}</span>
                  <span style={styles.tipTaskCount}>{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ), document.body)}
      </>
      )}
    </div>
  );
};

// ── By-department breakdown ─────────────────────────────────────────────────────
// Pick a department; see each user's created/completed counts per task type.
const DepartmentBreakdown = ({ departments = [], onPickUser }) => {
  const showToast = useToast();
  const isDesktop = useIsDesktop();
  const [deptId, setDeptId] = useState(departments[0]?._id || '');
  const [metric, setMetric] = useState('completed'); // 'completed' | 'created'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!deptId) return undefined;
    let cancelled = false;
    setLoading(true);
    tasksApi.analyticsByDepartment(deptId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) showToast('Failed to load department analytics', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [deptId, showToast]);

  const taskTypes = data?.taskTypes || [];
  const valueOf = (row, tid) => ((metric === 'completed' ? row.completed : row.created)[tid]) || 0;
  const totalOf = (row) => (metric === 'completed' ? row.completedTotal : row.createdTotal);
  const rows = useMemo(
    () => [...(data?.rows || [])].sort((a, b) => totalOf(b) - totalOf(a)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, metric]
  );
  const grandTotal = rows.reduce((s, r) => s + totalOf(r), 0);

  if (departments.length === 0) {
    return <div style={styles.empty}><Icon name="account_tree" size={36} color={colors.textFaint} /><p style={{ marginTop: 10 }}>No departments you can view.</p></div>;
  }

  return (
    <div>
      <div style={styles.deptControls}>
        <div style={styles.deptField}>
          <label style={styles.deptLabel}>Department</label>
          <select style={styles.deptSelect} value={deptId} onChange={(e) => setDeptId(e.target.value)}>
            {departments.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
          </select>
        </div>
        <div style={styles.metricToggle}>
          <button style={subNavPill(metric === 'completed')} onClick={() => setMetric('completed')}>
            <Icon name="check_circle" size={15} color={metric === 'completed' ? colors.parGreenBright : colors.text} /> Completed
          </button>
          <button style={subNavPill(metric === 'created')} onClick={() => setMetric('created')}>
            <Icon name="add_task" size={15} color={metric === 'created' ? colors.parGreenBright : colors.text} /> Created
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading…</div>
      ) : rows.length === 0 || taskTypes.length === 0 ? (
        <div style={styles.empty}>
          <Icon name="bar_chart" size={36} color={colors.textFaint} />
          <p style={{ marginTop: 10 }}>No tasks in this department yet.</p>
        </div>
      ) : isDesktop ? (
        <div style={styles.scroll}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.thUser }}>Person</th>
                {taskTypes.map((t) => (
                  <th key={t._id} style={styles.th} title={t.name}>
                    <span style={styles.thType}><Icon name={t.icon || 'task'} size={14} color={colors.textMuted} /> {t.name}</span>
                  </th>
                ))}
                <th style={{ ...styles.th, ...styles.thTotal }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId} style={styles.tr}>
                  <td style={{ ...styles.td, ...styles.tdUser }}>
                    <Avatar user={{ name: r.name, picture: r.picture }} size={24} />
                    <button
                      type="button"
                      style={styles.nameBtn}
                      onClick={() => onPickUser && onPickUser({ userId: r.userId, name: r.name, picture: r.picture })}
                      title={`View ${r.name}'s report`}
                    >
                      {r.name}
                    </button>
                  </td>
                  {taskTypes.map((t) => {
                    const v = valueOf(r, t._id);
                    return <td key={t._id} style={{ ...styles.td, ...styles.tdNum, color: v ? colors.text : colors.textFaint }}>{v || '·'}</td>;
                  })}
                  <td style={{ ...styles.td, ...styles.tdNum, ...styles.tdTotal }}>{totalOf(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Mobile: a card per person with per-type chips.
        <div style={styles.mobileList}>
          {rows.map((r) => (
            <div key={r.userId} style={styles.mUser}>
              <div style={styles.mNameLine}>
                <Avatar user={{ name: r.name, picture: r.picture }} size={24} />
                <button
                  type="button"
                  style={styles.nameBtn}
                  onClick={() => onPickUser && onPickUser({ userId: r.userId, name: r.name, picture: r.picture })}
                  title={`View ${r.name}'s report`}
                >
                  {r.name}
                </button>
                <span style={styles.nameTotal}>{totalOf(r)}</span>
              </div>
              <div style={styles.chipWrap}>
                {taskTypes.map((t) => {
                  const v = valueOf(r, t._id);
                  if (!v) return null;
                  return <span key={t._id} style={styles.typeChip}><Icon name={t.icon || 'task'} size={12} color={colors.textMuted} /> {t.name}: <strong>{v}</strong></span>;
                })}
                {totalOf(r) === 0 && <span style={styles.noneChip}>None</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div style={styles.bdFootnote}>
          {grandTotal} {metric} across {rows.length} {rows.length === 1 ? 'person' : 'people'}. “Completed” counts who marked a task done.
        </div>
      )}
    </div>
  );
};

// ── Per-person report ───────────────────────────────────────────────────────
// Everything one person did between two dates, grouped by task: what they
// created, what they ticked off (with created + clicked-done timestamps),
// what they wrote, and their lead actions — plus a full chronological log.
const UserReport = ({ user, onBack }) => {
  const showToast = useToast();
  const isDesktop = useIsDesktop();
  const { actionLabel } = useLeadActionMeta();
  const [from, setFrom] = useState(localDay(daysAgo(29)));
  const [to, setTo] = useState(localDay(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [showAllCreated, setShowAllCreated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    tasksApi.userReport(user.userId, from, to)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) showToast('Failed to load report', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.userId, from, to, showToast]);

  const preset = (days) => {
    setFrom(localDay(daysAgo(days - 1)));
    setTo(localDay(new Date()));
  };
  const presetThisMonth = () => {
    const now = new Date();
    setFrom(localDay(new Date(now.getFullYear(), now.getMonth(), 1)));
    setTo(localDay(now));
  };

  const totals = data?.totals || {};
  const tasks = data?.tasks || [];
  const createdOnly = data?.createdOnly || [];
  const leads = data?.leads || [];

  const exportCsv = () => {
    if (!data) return;
    const rows = [['When', 'Action', 'Task / Client', 'Detail', 'Points']];
    (data.timeline || []).forEach((t) => {
      rows.push([fmtDateTime(t.at), actionLabel(t.type), t.title, t.detail || '', t.points]);
    });
    const csv = rows
      .map((r) => r.map((c) => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(user.name).replace(/\s+/g, '-')}_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SUMMARY = [
    { key: 'points', label: 'Points', icon: 'stars' },
    { key: 'tasksCreated', label: 'Tasks created', icon: 'add_task' },
    { key: 'tasksCompleted', label: 'Tasks completed', icon: 'task_alt' },
    { key: 'todosCompleted', label: 'Checklist done', icon: 'check_circle' },
    { key: 'messages', label: 'Messages', icon: 'forum' },
    { key: 'actions', label: 'Actions', icon: 'bolt' }
  ];

  return (
    <div>
      <div style={styles.reportHead}>
        <button type="button" style={styles.backBtn} onClick={onBack}>
          <Icon name="arrow_back" size={16} color={colors.text} /> Back
        </button>
        <div style={styles.reportWho}>
          <Avatar user={{ name: user.name, picture: user.picture }} size={34} />
          <div>
            <div style={styles.reportName}>{user.name}</div>
            <div style={styles.reportSub}>
              {data ? `${fmtDate(data.range.from)} – ${fmtDate(data.range.to)}` : 'Performance report'}
            </div>
          </div>
        </div>
        <button type="button" style={styles.csvBtn} onClick={exportCsv} disabled={!data}>
          <Icon name="download" size={15} color={colors.parGreenBright} /> CSV
        </button>
      </div>

      <div style={styles.rangeBar}>
        <div style={styles.rangeField}>
          <label style={styles.deptLabel}>From</label>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={styles.dateInput} />
        </div>
        <div style={styles.rangeField}>
          <label style={styles.deptLabel}>To</label>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={styles.dateInput} />
        </div>
        <div style={styles.presets}>
          <button style={subNavPill(false)} onClick={() => preset(7)}>7 days</button>
          <button style={subNavPill(false)} onClick={() => preset(30)}>30 days</button>
          <button style={subNavPill(false)} onClick={presetThisMonth}>This month</button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading report…</div>
      ) : (
        <>
          <div style={styles.summaryCards}>
            {SUMMARY.map((s) => (
              <div key={s.key} style={styles.card}>
                <div style={styles.cardLabel}><Icon name={s.icon} size={13} color={colors.textMuted} /> {s.label}</div>
                <div style={styles.cardValue}>{totals[s.key] || 0}</div>
              </div>
            ))}
          </div>

          {tasks.length === 0 && leads.length === 0 && createdOnly.length === 0 ? (
            <div style={styles.empty}>
              <Icon name="event_busy" size={36} color={colors.textFaint} />
              <p style={{ marginTop: 10 }}>Nothing recorded for {user.name} in this date range.</p>
            </div>
          ) : (
            <>
              {tasks.map((t) => (
                <div key={t.taskId} style={styles.rTask}>
                  <div style={styles.rTaskHead}>
                    <Icon name={t.typeIcon || 'task'} size={16} color={colors.textMuted} />
                    <span style={styles.rTaskTitle}>{t.title}</span>
                    {t.status && (
                      <span style={{ ...styles.rStatus, color: statusMeta(t.status).color, borderColor: statusMeta(t.status).color }}>
                        {statusMeta(t.status).label}
                      </span>
                    )}
                    {t.points > 0 && <span style={styles.rPoints}>{t.points} pt{t.points === 1 ? '' : 's'}</span>}
                  </div>

                  <div style={styles.rMeta}>
                    {t.taskType && <span style={styles.rMetaChip}>{t.taskType}</span>}
                    {(t.departments || []).map((d) => <span key={d} style={styles.rMetaChip}>{d}</span>)}
                    <span style={styles.rMetaItem}>
                      <Icon name="schedule" size={12} color={colors.textFaint} /> Created {fmtDate(t.taskCreatedAt)}
                      {t.createdByThem ? ' by them' : ''}
                    </span>
                    {t.completedAt && (
                      <span style={{ ...styles.rMetaItem, color: colors.parGreenBright }}>
                        <Icon name="task_alt" size={12} color={colors.parGreenBright} /> Marked done {fmtDateTime(t.completedAt)}
                      </span>
                    )}
                  </div>

                  {t.todos.length > 0 && (
                    <div style={styles.rSection}>
                      <div style={styles.rSectionHead}>Checklist items completed ({t.todos.length})</div>
                      {t.todos.map((c, i) => (
                        <div key={i} style={{ ...styles.rTodo, ...(isDesktop ? null : styles.rTodoMobile) }}>
                          <span style={styles.rTodoText}>
                            <Icon name="check_circle" size={14} color={colors.parGreenBright} /> {c.text}
                          </span>
                          <span style={styles.rTodoDates}>
                            added {fmtDate(c.createdAt)} · <strong style={styles.rDoneAt}>done {fmtDateTime(c.doneAt)}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {t.messages.length > 0 && (
                    <div style={styles.rSection}>
                      <div style={styles.rSectionHead}>Messages ({t.messages.length})</div>
                      {t.messages.map((m, i) => (
                        <div key={i} style={styles.rMsg}>
                          <span style={styles.rMsgAt}>{fmtDateTime(m.at)}</span>
                          <span style={styles.rMsgText}>
                            {m.text || <em style={{ color: colors.textFaint }}>no text</em>}
                            {m.attachments > 0 && (
                              <span style={styles.rAttach}>
                                <Icon name="attach_file" size={11} color={colors.textMuted} />{m.attachments}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {createdOnly.length > 0 && (
                <div style={styles.rTask}>
                  <div style={styles.rTaskHead}>
                    <Icon name="add_task" size={16} color={colors.textMuted} />
                    <span style={styles.rTaskTitle}>Tasks created (no further activity yet)</span>
                    <span style={styles.rPoints}>{createdOnly.length}</span>
                  </div>
                  <div style={styles.rSection}>
                    {(showAllCreated ? createdOnly : createdOnly.slice(0, 15)).map((t) => (
                      <div key={t.taskId} style={styles.rMsg}>
                        <span style={styles.rMsgAt}>{fmtDate(t.taskCreatedAt)}</span>
                        <span style={styles.rMsgText}>
                          {t.title}
                          {(t.departments || []).length > 0 && (
                            <span style={styles.rCreatedDept}> · {t.departments.join(', ')}</span>
                          )}
                        </span>
                      </div>
                    ))}
                    {createdOnly.length > 15 && (
                      <button type="button" style={styles.logToggle} onClick={() => setShowAllCreated((s) => !s)}>
                        {showAllCreated ? 'Show fewer' : `Show all ${createdOnly.length}`}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {leads.length > 0 && (
                <div style={styles.rTask}>
                  <div style={styles.rTaskHead}>
                    <Icon name="pin_drop" size={16} color={colors.textMuted} />
                    <span style={styles.rTaskTitle}>Client / lead actions</span>
                  </div>
                  {leads.map((l) => (
                    <div key={l.markerId} style={styles.rSection}>
                      <div style={styles.rSectionHead}>{l.name} · {l.points} pt{l.points === 1 ? '' : 's'}</div>
                      {l.actions.map((a, i) => (
                        <div key={i} style={styles.rMsg}>
                          <span style={styles.rMsgAt}>{fmtDateTime(a.at)}</span>
                          <span style={styles.rMsgText}>{actionLabel(a.type)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              <button type="button" style={styles.logToggle} onClick={() => setShowLog((s) => !s)}>
                <Icon name={showLog ? 'expand_more' : 'chevron_right'} size={16} color={colors.textMuted} />
                Full activity log ({(data?.timeline || []).length})
              </button>

              {showLog && (
                <div style={styles.scroll}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={{ ...styles.th, textAlign: 'left' }}>When</th>
                        <th style={{ ...styles.th, textAlign: 'left' }}>Action</th>
                        <th style={{ ...styles.th, textAlign: 'left' }}>Task / Client</th>
                        <th style={{ ...styles.th, textAlign: 'left' }}>Detail</th>
                        <th style={styles.th}>Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.timeline || []).map((row, i) => (
                        <tr key={i} style={styles.tr}>
                          <td style={{ ...styles.td, whiteSpace: 'nowrap', color: colors.textMuted }}>{fmtDateTime(row.at)}</td>
                          <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{actionLabel(row.type)}</td>
                          <td style={styles.td}>{row.title}</td>
                          <td style={{ ...styles.td, color: colors.textMuted }}>{row.detail}</td>
                          <td style={{ ...styles.td, ...styles.tdNum }}>{row.points || '·'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  viewTabs: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  title: { display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 18, color: colors.text },
  periods: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  loading: { padding: '30px', textAlign: 'center', color: colors.textMuted },
  summaryCards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 18 },
  card: { background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md, padding: 14 },
  cardLabel: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 },
  cardValue: { fontSize: 24, fontWeight: 800, color: colors.text, marginTop: 6 },
  empty: { padding: '40px 16px', textAlign: 'center', color: colors.textMuted },
  scroll: { overflowX: 'auto', paddingBottom: 6 },
  gridRow: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 },
  mobileList: { display: 'flex', flexDirection: 'column', gap: 12 },
  mUser: { background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: radii.md, padding: 10 },
  mNameLine: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  mBlocksRow: { display: 'flex', alignItems: 'center', gap: 4 },
  nameCell: { width: 168, minWidth: 168, display: 'flex', alignItems: 'center', gap: 8, paddingRight: 8 },
  nameText: { fontSize: 13, fontWeight: 600, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 },
  nameTotal: { fontSize: 11, fontWeight: 800, color: colors.parGreenBright, minWidth: 20, textAlign: 'right' },
  colLabel: { fontSize: 10, color: colors.textFaint, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden' },
  cell: {
    height: 30,
    flexShrink: 0,
    borderRadius: 6,
    border: '1px solid',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 800
  },
  legend: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, flexWrap: 'wrap' },
  legendText: { fontSize: 11, color: colors.textFaint },
  legendBox: { width: 16, height: 16, borderRadius: 4, border: '1px solid' },
  legendNote: { fontSize: 11.5, color: colors.textMuted, marginLeft: 10 },
  tooltip: {
    position: 'fixed',
    zIndex: 1000,
    pointerEvents: 'none',
    width: 240,
    background: colors.menuBg,
    border: `1px solid ${colors.border}`,
    borderRadius: radii.md,
    boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
    padding: 12,
    color: colors.text
  },
  tipHead: { fontSize: 12.5, fontWeight: 800, color: colors.text },
  tipCount: { fontSize: 12, color: colors.parGreenBright, fontWeight: 700, marginTop: 2 },
  tipActions: { color: colors.textMuted, fontWeight: 600 },
  tipTypes: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  tipType: { fontSize: 11, color: colors.textMuted, background: colors.surface2, borderRadius: 999, padding: '2px 8px' },
  tipTasks: { marginTop: 8, borderTop: `1px solid ${colors.borderSoft}`, paddingTop: 6 },
  tipTasksHead: { fontSize: 10.5, fontWeight: 800, color: colors.textFaint, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
  tipTask: { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '2px 0' },
  tipTaskTitle: { color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tipTaskCount: { color: colors.parGreenBright, fontWeight: 700 },

  // By-department breakdown
  deptControls: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  deptField: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200, flex: 1 },
  deptLabel: { fontSize: 11.5, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 },
  deptSelect: {
    padding: '10px 12px', borderRadius: radii.sm, border: `1px solid ${colors.border}`,
    background: colors.inputBg, color: colors.text, fontSize: 15, fontWeight: 600, fontFamily: 'inherit', boxSizing: 'border-box'
  },
  metricToggle: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  table: { borderCollapse: 'collapse', width: '100%', minWidth: 480 },
  th: { textAlign: 'center', padding: '8px 10px', fontSize: 11.5, fontWeight: 700, color: colors.textMuted, borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap' },
  thUser: { textAlign: 'left', position: 'sticky', left: 0, background: colors.surface, zIndex: 1 },
  thType: { display: 'inline-flex', alignItems: 'center', gap: 5 },
  thTotal: { color: colors.parGreenBright },
  tr: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { padding: '8px 10px', fontSize: 13.5, verticalAlign: 'middle' },
  tdUser: { display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', left: 0, background: colors.surface, minWidth: 150 },
  tdNum: { textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  tdTotal: { color: colors.parGreenBright, fontWeight: 800 },
  nameBtn: {
    flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 0, padding: 0,
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: colors.text, cursor: 'pointer',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: 'underline',
    textDecorationColor: colors.borderSoft, textUnderlineOffset: 3
  },

  // Per-person report
  reportHead: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: radii.sm,
    border: `1px solid ${colors.border}`, background: colors.surfaceSoft, color: colors.text,
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer'
  },
  reportWho: { display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 },
  reportName: { fontSize: 17, fontWeight: 800, color: colors.text },
  reportSub: { fontSize: 12, color: colors.textMuted, marginTop: 1 },
  csvBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: radii.sm,
    border: `1px solid ${colors.border}`, background: colors.surfaceSoft, color: colors.text,
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer'
  },
  rangeBar: { display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 18 },
  rangeField: { display: 'flex', flexDirection: 'column', gap: 6 },
  dateInput: {
    padding: '9px 12px', borderRadius: radii.sm, border: `1px solid ${colors.border}`,
    background: colors.inputBg, color: colors.text, fontSize: 14, fontWeight: 600,
    fontFamily: 'inherit', boxSizing: 'border-box'
  },
  presets: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  rTask: {
    background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.md, padding: 14, marginBottom: 12
  },
  rTaskHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  rTaskTitle: { fontSize: 14.5, fontWeight: 700, color: colors.text, flex: 1, minWidth: 0 },
  rStatus: { fontSize: 11, fontWeight: 700, border: '1px solid', borderRadius: 999, padding: '2px 8px' },
  rPoints: { fontSize: 11.5, fontWeight: 800, color: colors.parGreenBright },
  rMeta: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 },
  rMetaChip: { fontSize: 11, color: colors.textMuted, background: colors.surface2, borderRadius: 999, padding: '2px 9px' },
  rMetaItem: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: colors.textFaint },
  rSection: { marginTop: 10, borderTop: `1px solid ${colors.borderSoft}`, paddingTop: 8 },
  rSectionHead: {
    fontSize: 10.5, fontWeight: 800, color: colors.textFaint, textTransform: 'uppercase',
    letterSpacing: 0.4, marginBottom: 6
  },
  rTodo: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '3px 0' },
  rTodoMobile: { flexDirection: 'column', alignItems: 'flex-start', gap: 2 },
  rTodoText: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.text, minWidth: 0 },
  rTodoDates: { fontSize: 11.5, color: colors.textFaint, whiteSpace: 'nowrap' },
  rDoneAt: { color: colors.parGreenBright, fontWeight: 700 },
  rMsg: { display: 'flex', gap: 10, padding: '3px 0', fontSize: 12.5 },
  rMsgAt: { color: colors.textFaint, whiteSpace: 'nowrap', minWidth: 148 },
  rMsgText: { color: colors.text, flex: 1, minWidth: 0 },
  rAttach: { display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 6, fontSize: 11, color: colors.textMuted },
  rCreatedDept: { color: colors.textFaint },
  logToggle: {
    display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 0, padding: '6px 0',
    color: colors.textMuted, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', marginTop: 4
  },

  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  typeChip: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: colors.textMuted, background: colors.surface2, borderRadius: 999, padding: '3px 10px' },
  noneChip: { fontSize: 12, color: colors.textFaint, fontStyle: 'italic' },
  bdFootnote: { marginTop: 14, fontSize: 12, color: colors.textFaint }
};

export default TaskAnalytics;
